import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ticketsApi } from "@/lib/tickets-api";

/*
 * The document the message is shown in. The server has already sanitized the HTML; on top of that:
 * - the iframe runs no scripts (sandbox without allow-scripts),
 * - this Content-Security-Policy lets it load only the archive's own images and inline styles,
 * - links open in a new tab, outside the archive.
 * allow-same-origin is kept (with scripts still off) so the archive's session reaches the inline images and
 * the page can measure the message to size the frame.
 */
const FRAME_HEAD =
  '<!doctype html><html><head><meta charset="utf-8">' +
  "<meta http-equiv=\"Content-Security-Policy\" content=\"default-src 'none'; img-src 'self' data:; " +
  "style-src 'unsafe-inline'; font-src data:\">" +
  '<base target="_blank">' +
  "<style>html,body{margin:0;background:#fff;color:#1d2530}" +
  'body{padding:16px 20px;font:14px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif;overflow-wrap:anywhere}' +
  "img{max-width:100%;height:auto}table{max-width:100%}pre{white-space:pre-wrap}</style></head><body>";

export function HtmlMessage({ articleId }: { articleId: number }) {
  const { t } = useTranslation();
  const message = useQuery({
    queryKey: ["article-html", articleId],
    queryFn: () => ticketsApi.articleHtml(articleId),
    staleTime: Infinity,
  });
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);

  const observer = useRef<ResizeObserver | null>(null);

  const measure = useCallback(() => {
    // The body's own height: the document's scrollHeight never drops below the frame's current height.
    const body = frame.current?.contentDocument?.body;
    if (body) setHeight(Math.max(48, Math.ceil(body.getBoundingClientRect().height)));
  }, []);

  // Once the message is in, follow its size: images arriving later make it taller.
  const onLoad = () => {
    measure();
    observer.current?.disconnect();
    const body = frame.current?.contentDocument?.body;
    if (body && typeof ResizeObserver !== "undefined") {
      observer.current = new ResizeObserver(measure);
      observer.current.observe(body);
    }
  };
  useEffect(() => () => observer.current?.disconnect(), []);

  if (message.isError) return <p className="px-5 py-4 text-sm text-danger">{t("ticket.htmlError")}</p>;
  if (!message.data) return <p className="px-5 py-4 text-sm text-ink-muted">{t("common.loading")}</p>;
  const { html, blocked_remote, missing_inline } = message.data;
  return (
    <div className="space-y-2 px-5 py-4">
      {(blocked_remote > 0 || missing_inline > 0) && (
        <p className="text-xs text-ink-subtle">
          {blocked_remote > 0 && t("ticket.remoteBlocked", { count: blocked_remote })}{" "}
          {missing_inline > 0 && t("ticket.inlineMissing", { count: missing_inline })}
        </p>
      )}
      <iframe
        ref={frame}
        title={t("ticket.htmlFrame")}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        srcDoc={`${FRAME_HEAD}${html}</body></html>`}
        onLoad={onLoad}
        style={{ height }}
        className="block w-full rounded-control border border-line bg-white"
      />
    </div>
  );
}
